import '../style/CombosOffers.css';
import React, { useEffect, useState, useCallback, useMemo } from 'react';
import {
  Plus, Search, Tag, Percent, IndianRupee, Trash2, Pencil,
  X, ChevronRight, ChevronLeft, ImagePlus, AlertCircle,
  PackageOpen, Loader2, Check, Layers, ListChecks, Clock, CalendarDays
} from 'lucide-react';
import { host } from '../script/variables';
import uploadViaPresign from '../script/uploadViaPresignV2';

const getToken = () => localStorage.getItem('partnertoken');
const SIZE_KEYS = ['small', 'medium', 'large'];

const dishSizes = (dish) => SIZE_KEYS.filter((s) => dish.sizes?.[s]?.price > 0);
const dishPrice = (dish, size) => (size ? dish.sizes?.[size]?.price || 0 : 0);

const DAYS = [
  { value: 0, label: 'Sun' }, { value: 1, label: 'Mon' }, { value: 2, label: 'Tue' },
  { value: 3, label: 'Wed' }, { value: 4, label: 'Thu' }, { value: 5, label: 'Fri' }, { value: 6, label: 'Sat' },
];

/* ─── Toggle ─── */
const Toggle = ({ on, loading, onChange }) => (
  <button
    type="button"
    className={`co-tog ${on ? 'co-tog--on' : 'co-tog--off'}`}
    onClick={onChange}
    disabled={loading}
    aria-pressed={on}
    title={on ? 'Mark as unavailable' : 'Mark as available'}
  >
    <span className="co-tog__thumb" />
  </button>
);

/* ─── Combo Card ─── */
const ComboCard = ({ combo, onEdit, onDelete, onToggle, busyId }) => {
  const { pricing } = combo;
  const isBusy = busyId === combo._id;
  const isChoice = combo.comboStructure === 'CHOICE';
  const itemCount = isChoice ? (combo.choiceGroups?.length || 0) : (combo.dishes?.length || 0);
  const hasSchedule = combo.validFrom || combo.validTill || combo.startTime || (combo.availableDays?.length > 0);

  return (
    <div className={`co-card ${!combo.isAvailable ? 'co-card--off' : ''}`}>
      <div className="co-card__img">
        {combo.image ? (
          <img src={combo.image} alt={combo.name} />
        ) : (
          <div className="co-card__img-empty"><PackageOpen /></div>
        )}
        {combo.tag && <span className="co-card__tag">{combo.tag}</span>}
        {pricing?.hasUnavailableDish && (
          <span className="co-card__warn" title="One or more dishes in this combo are unavailable">
            <AlertCircle size={14} /> Needs attention
          </span>
        )}
        {combo.isAvailable && combo.isCurrentlyActive === false && (
          <span className="co-card__schedule" title="This combo is scheduled but not visible to customers right now">
            <Clock size={12} /> Not live right now
          </span>
        )}
      </div>

      <div className="co-card__body">
        <div className="co-card__head">
          <p className="co-card__name" title={combo.name}>{combo.name}</p>
          <Toggle on={combo.isAvailable} loading={isBusy} onChange={() => onToggle(combo)} />
        </div>

        <p className="co-card__meta">
          {isChoice ? <ListChecks size={12} /> : <Layers size={12} />}
          {isChoice ? `${itemCount} choice group${itemCount !== 1 ? 's' : ''}` : `${itemCount} item${itemCount !== 1 ? 's' : ''}`}
          {' · '}{combo.dietType}
        </p>

        {hasSchedule && (
          <p className="co-card__sched-line">
            <CalendarDays size={12} />
            {combo.startTime && combo.endTime ? `${combo.startTime}–${combo.endTime}` : 'Scheduled'}
            {combo.availableDays?.length > 0 && ` · ${combo.availableDays.map((d) => DAYS.find((x) => x.value === d)?.label).join(', ')}`}
          </p>
        )}

        <div className="co-card__price">
          <span className="co-card__final">₹{pricing?.finalPrice ?? 0}{isChoice && <span className="co-card__from">from</span>}</span>
          {!combo.isFixedPrice && pricing?.originalPrice > pricing?.finalPrice && (
            <>
              <span className="co-card__original">₹{pricing.originalPrice}</span>
              <span className="co-card__save">{pricing.savingsPercent}% off</span>
            </>
          )}
          {combo.isFixedPrice && <span className="co-card__badge">Fixed price</span>}
        </div>

        <div className="co-card__actions">
          <button className="co-btn-ghost" onClick={() => onEdit(combo)}>
            <Pencil size={15} /> Edit
          </button>
          <button className="co-btn-ghost co-btn-ghost--danger" onClick={() => onDelete(combo)}>
            <Trash2 size={15} /> Delete
          </button>
        </div>
      </div>
    </div>
  );
};

/* ─── Dish picker row (used by both BUNDLE list and CHOICE group options) ─── */
const DishPickRow = ({ dish, selected, onToggle, onQtyChange, onSizeChange }) => {
  const sizes = dishSizes(dish);
  const activeSize = selected?.size || sizes[0] || null;
  const price = activeSize ? dishPrice(dish, activeSize) : null;

  return (
    <div className={`co-pick ${selected ? 'co-pick--on' : ''}`}>
      <button type="button" className="co-pick__check" onClick={() => onToggle(dish)}>
        {selected && <Check size={13} />}
      </button>

      <div className="co-pick__info">
        <p className="co-pick__name">{dish.name}</p>
        <p className="co-pick__price">{price ? `₹${price}` : 'No price set'}</p>
      </div>

      {selected && (
        <div className="co-pick__controls">
          {sizes.length > 1 && (
            <select className="co-pick__size" value={activeSize} onChange={(e) => onSizeChange(dish._id, e.target.value)}>
              {sizes.map((s) => <option key={s} value={s}>{s[0].toUpperCase() + s.slice(1)}</option>)}
            </select>
          )}
          <div className="co-pick__qty">
            <button type="button" onClick={() => onQtyChange(dish._id, -1)}>−</button>
            <span>{selected.quantity}</span>
            <button type="button" onClick={() => onQtyChange(dish._id, 1)}>+</button>
          </div>
        </div>
      )}
    </div>
  );
};

/* ─── Searchable dish list (shared) ─── */
const DishSearchList = ({ allDishes, selectedMap, onToggle, onQtyChange, onSizeChange }) => {
  const [q, setQ] = useState('');
  const filtered = useMemo(
    () => (q ? allDishes.filter((d) => d.name.toLowerCase().includes(q.toLowerCase())) : allDishes),
    [q, allDishes]
  );

  return (
    <div className="co-dish-search-block">
      <div className="co-dish-search">
        <Search size={16} />
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search your menu…" />
      </div>
      <div className="co-pick-list">
        {filtered.length === 0 ? (
          <p className="co-empty-text">No dishes found</p>
        ) : (
          filtered.map((dish) => (
            <DishPickRow
              key={dish._id}
              dish={dish}
              selected={selectedMap[dish._id]}
              onToggle={onToggle}
              onQtyChange={onQtyChange}
              onSizeChange={onSizeChange}
            />
          ))
        )}
      </div>
    </div>
  );
};

/* ─── Choice group card (builder for one "Choose your X" group) ─── */
const ChoiceGroupCard = ({ group, index, allDishes, onUpdate, onRemove }) => {
  const [expanded, setExpanded] = useState(true);
  const selectedCount = Object.keys(group.options).length;

  const toggleOption = (dish) => {
    const next = { ...group.options };
    if (next[dish._id]) {
      delete next[dish._id];
    } else {
      const firstSize = dishSizes(dish)[0] || null;
      next[dish._id] = { size: firstSize, quantity: 1 };
    }
    onUpdate({ ...group, options: next });
  };

  const changeQty = (dishId, delta) => {
    const current = group.options[dishId];
    if (!current) return;
    onUpdate({
      ...group,
      options: { ...group.options, [dishId]: { ...current, quantity: Math.max(1, current.quantity + delta) } },
    });
  };

  const changeSize = (dishId, size) => {
    onUpdate({ ...group, options: { ...group.options, [dishId]: { ...group.options[dishId], size } } });
  };

  return (
    <div className="co-group">
      <div className="co-group__head">
        <input
          className="co-group__name"
          value={group.groupName}
          onChange={(e) => onUpdate({ ...group, groupName: e.target.value })}
          placeholder={`Group ${index + 1} name, e.g. "Choose your starter"`}
        />
        <button type="button" className="co-group__remove" onClick={onRemove}><Trash2 size={15} /></button>
      </div>

      <div className="co-group__limits">
        <label>
          <span>Min pick</span>
          <input
            type="number" min="1"
            value={group.minSelect}
            onChange={(e) => onUpdate({ ...group, minSelect: Math.max(1, Number(e.target.value) || 1) })}
          />
        </label>
        <label>
          <span>Max pick</span>
          <input
            type="number" min="1"
            value={group.maxSelect}
            onChange={(e) => onUpdate({ ...group, maxSelect: Math.max(1, Number(e.target.value) || 1) })}
          />
        </label>
        <span className="co-group__count">{selectedCount} dish{selectedCount !== 1 ? 'es' : ''} added</span>
        <button type="button" className="co-group__toggle" onClick={() => setExpanded((v) => !v)}>
          {expanded ? 'Hide dishes' : 'Show dishes'}
        </button>
      </div>

      {expanded && (
        <DishSearchList
          allDishes={allDishes}
          selectedMap={group.options}
          onToggle={toggleOption}
          onQtyChange={changeQty}
          onSizeChange={changeSize}
        />
      )}
    </div>
  );
};

/* ─── Create / Edit Wizard ─── */
const ComboWizard = ({ cafeid, editingCombo, allDishes, onClose, onSaved, onError }) => {
  const isEdit = !!editingCombo;
  const [step, setStep] = useState(1);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);

  const [form, setForm] = useState(() => {
    if (editingCombo) {
      const isChoice = editingCombo.comboStructure === 'CHOICE';
      return {
        name: editingCombo.name || '',
        description: editingCombo.description || '',
        image: editingCombo.image || '',
        tag: editingCombo.tag || '',
        comboStructure: editingCombo.comboStructure || 'BUNDLE',
        isFixedPrice: editingCombo.isFixedPrice,
        fixedPrice: editingCombo.fixedPrice || '',
        discountType: editingCombo.discountType || 'PERCENT',
        discount: editingCombo.discount || '',
        selectedDishes: !isChoice
          ? (editingCombo.dishes || []).reduce((acc, d) => {
              const id = d.dishId?._id || d.dishId;
              acc[id] = { size: d.size, quantity: d.quantity };
              return acc;
            }, {})
          : {},
        choiceGroups: isChoice
          ? (editingCombo.choiceGroups || []).map((g) => ({
              groupName: g.groupName,
              minSelect: g.minSelect || 1,
              maxSelect: g.maxSelect || 1,
              options: (g.options || []).reduce((acc, o) => {
                const id = o.dishId?._id || o.dishId;
                acc[id] = { size: o.size, quantity: o.quantity };
                return acc;
              }, {}),
            }))
          : [],
        validFrom: editingCombo.validFrom ? String(editingCombo.validFrom).slice(0, 10) : '',
        validTill: editingCombo.validTill ? String(editingCombo.validTill).slice(0, 10) : '',
        availableDays: editingCombo.availableDays || [],
        startTime: editingCombo.startTime || '',
        endTime: editingCombo.endTime || '',
        allDay: !editingCombo.startTime,
      };
    }
    return {
      name: '',
      description: '',
      image: '',
      tag: '',
      comboStructure: 'BUNDLE',
      isFixedPrice: true,
      fixedPrice: '',
      discountType: 'PERCENT',
      discount: '',
      selectedDishes: {},
      choiceGroups: [],
      validFrom: '',
      validTill: '',
      availableDays: [],
      startTime: '',
      endTime: '',
      allDay: true,
    };
  });

  const update = (key, val) => setForm((f) => ({ ...f, [key]: val }));

  /* ── BUNDLE dish selection ── */
  const toggleDish = (dish) => {
    setForm((f) => {
      const next = { ...f.selectedDishes };
      if (next[dish._id]) delete next[dish._id];
      else next[dish._id] = { size: dishSizes(dish)[0] || null, quantity: 1 };
      return { ...f, selectedDishes: next };
    });
  };
  const changeQty = (dishId, delta) => {
    setForm((f) => {
      const current = f.selectedDishes[dishId];
      if (!current) return f;
      return { ...f, selectedDishes: { ...f.selectedDishes, [dishId]: { ...current, quantity: Math.max(1, current.quantity + delta) } } };
    });
  };
  const changeSize = (dishId, size) => {
    setForm((f) => ({ ...f, selectedDishes: { ...f.selectedDishes, [dishId]: { ...f.selectedDishes[dishId], size } } }));
  };

  /* ── CHOICE group management ── */
  const addGroup = () => {
    setForm((f) => ({
      ...f,
      choiceGroups: [...f.choiceGroups, { groupName: '', minSelect: 1, maxSelect: 1, options: {} }],
    }));
  };
  const updateGroup = (index, updatedGroup) => {
    setForm((f) => {
      const next = [...f.choiceGroups];
      next[index] = updatedGroup;
      return { ...f, choiceGroups: next };
    });
  };
  const removeGroup = (index) => {
    setForm((f) => ({ ...f, choiceGroups: f.choiceGroups.filter((_, i) => i !== index) }));
  };

  /* ── Availability ── */
  const toggleDay = (dayValue) => {
    setForm((f) => {
      const has = f.availableDays.includes(dayValue);
      return { ...f, availableDays: has ? f.availableDays.filter((d) => d !== dayValue) : [...f.availableDays, dayValue] };
    });
  };

  const selectedCount = form.comboStructure === 'BUNDLE'
    ? Object.keys(form.selectedDishes).length
    : form.choiceGroups.length;

  /* ── live price preview, mirrors backend logic ── */
  const livePricing = useMemo(() => {
    let originalPrice = 0;

    if (form.comboStructure === 'BUNDLE') {
      for (const [dishId, sel] of Object.entries(form.selectedDishes)) {
        const dish = allDishes.find((d) => d._id === dishId);
        if (!dish) continue;
        originalPrice += dishPrice(dish, sel.size) * sel.quantity;
      }
    } else {
      for (const group of form.choiceGroups) {
        const lines = Object.entries(group.options).map(([dishId, sel]) => {
          const dish = allDishes.find((d) => d._id === dishId);
          return dish ? dishPrice(dish, sel.size) * sel.quantity : 0;
        });
        const cheapest = lines.length ? Math.min(...lines) : 0;
        originalPrice += cheapest * (group.minSelect || 1);
      }
    }

    let finalPrice = originalPrice;
    if (form.isFixedPrice) {
      finalPrice = Number(form.fixedPrice) || 0;
    } else if (form.discountType === 'PERCENT') {
      finalPrice = originalPrice - (originalPrice * (Number(form.discount) || 0)) / 100;
    } else {
      finalPrice = originalPrice - (Number(form.discount) || 0);
    }
    return { originalPrice: Math.round(originalPrice), finalPrice: Math.max(0, Math.round(finalPrice)) };
  }, [form, allDishes]);

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    onError('');
    try {
      const url = await uploadViaPresign(file, 'dish');
      update('image', url);
    } catch (err) {
      onError(err.message || 'Image upload failed');
    } finally {
      setUploading(false);
    }
  };

  const stepLabels = ['Details', 'Type', 'Pricing', 'Dishes', 'Availability', 'Review'];

  const validateStep = (target) => {
    if (target === 2 && !form.name.trim()) {
      onError('Give your combo a name before continuing');
      return false;
    }
    if (target === 4) {
      if (form.isFixedPrice && (!form.fixedPrice || Number(form.fixedPrice) <= 0)) {
        onError('Enter a fixed price greater than 0');
        return false;
      }
      if (!form.isFixedPrice && (form.discount === '' || Number(form.discount) < 0)) {
        onError('Enter a valid discount value');
        return false;
      }
    }
    if (target === 5) {
      if (form.comboStructure === 'BUNDLE' && Object.keys(form.selectedDishes).length === 0) {
        onError('Select at least one dish for the combo');
        return false;
      }
      if (form.comboStructure === 'CHOICE') {
        if (form.choiceGroups.length === 0) {
          onError('Add at least one choice group, e.g. "Choose your starter"');
          return false;
        }
        for (const g of form.choiceGroups) {
          if (!g.groupName.trim()) { onError('Every choice group needs a name'); return false; }
          if (Object.keys(g.options).length === 0) { onError(`Add dishes to "${g.groupName || 'a group'}"`); return false; }
          if (g.maxSelect > Object.keys(g.options).length) {
            onError(`"${g.groupName}" allows picking ${g.maxSelect} but only has ${Object.keys(g.options).length} dish(es)`);
            return false;
          }
        }
      }
    }
    if (target === 6) {
      if (!form.allDay && (!form.startTime || !form.endTime)) {
        onError('Set both a start and end time, or switch to all-day');
        return false;
      }
      if (!form.allDay && form.startTime && form.endTime && form.startTime >= form.endTime) {
        onError('Start time must be before end time');
        return false;
      }
      if (form.validFrom && form.validTill && form.validFrom > form.validTill) {
        onError('Valid-from date must be before valid-till date');
        return false;
      }
    }
    return true;
  };

  const goNext = () => { if (!validateStep(step + 1)) return; onError(''); setStep((s) => Math.min(6, s + 1)); };
  const goBack = () => setStep((s) => Math.max(1, s - 1));

  const handleSubmit = async () => {
    if (!validateStep(6)) return;
    setSaving(true);
    onError('');
    try {
      const payload = {
        cafeId: cafeid,
        name: form.name.trim(),
        description: form.description.trim() || null,
        image: form.image || null,
        tag: form.tag.trim() || null,
        comboStructure: form.comboStructure,
        isFixedPrice: form.isFixedPrice,
        fixedPrice: form.isFixedPrice ? Number(form.fixedPrice) : undefined,
        discountType: form.discountType,
        discount: form.isFixedPrice ? undefined : Number(form.discount),
        validFrom: form.validFrom || null,
        validTill: form.validTill || null,
        availableDays: form.availableDays,
        startTime: form.allDay ? null : (form.startTime || null),
        endTime: form.allDay ? null : (form.endTime || null),
      };

      if (form.comboStructure === 'BUNDLE') {
        payload.dishes = Object.entries(form.selectedDishes).map(([dishId, sel]) => ({
          dishId, size: sel.size, quantity: sel.quantity,
        }));
      } else {
        payload.choiceGroups = form.choiceGroups.map((g) => ({
          groupName: g.groupName.trim(),
          minSelect: g.minSelect,
          maxSelect: g.maxSelect,
          options: Object.entries(g.options).map(([dishId, sel]) => ({
            dishId, size: sel.size, quantity: sel.quantity,
          })),
        }));
      }

      const url = isEdit ? `${host}/combo/update/${editingCombo._id}` : `${host}/combo/create`;
      const r = await fetch(url, {
        method: isEdit ? 'PATCH' : 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: getToken() },
        body: JSON.stringify(payload),
      });
      const d = await r.json();
      if (!r.ok || d.success === false) {
        onError(d.msg || 'Could not save combo');
        return;
      }
      onSaved();
    } catch {
      onError('Network error — please try again');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="co-modal-backdrop" onClick={onClose}>
      <div className="co-modal" onClick={(e) => e.stopPropagation()}>
        <div className="co-modal__head">
          <p className="co-modal__title">{isEdit ? 'Edit combo' : 'Create a combo'}</p>
          <button className="co-modal__close" onClick={onClose}><X /></button>
        </div>

        <div className="co-steps">
          {stepLabels.map((label, i) => (
            <div key={label} className={`co-step ${step === i + 1 ? 'co-step--active' : ''} ${step > i + 1 ? 'co-step--done' : ''}`}>
              <span className="co-step__dot">{step > i + 1 ? <Check size={12} /> : i + 1}</span>
              <span className="co-step__label">{label}</span>
            </div>
          ))}
        </div>

        <div className="co-modal__body">
          {step === 1 && (
            <div className="co-form">
              <label className="co-field">
                <span>Combo name</span>
                <input value={form.name} onChange={(e) => update('name', e.target.value)} placeholder="e.g. Family Feast" />
              </label>
              <label className="co-field">
                <span>Description (optional)</span>
                <textarea value={form.description} onChange={(e) => update('description', e.target.value)} placeholder="What's in this combo, in a line" rows={3} />
              </label>
              <label className="co-field">
                <span>Tag (optional)</span>
                <input value={form.tag} onChange={(e) => update('tag', e.target.value)} placeholder="e.g. Best Value" />
              </label>
              <label className="co-field">
                <span>Combo image (optional)</span>
                <div className="co-upload">
                  {form.image ? <img src={form.image} alt="Combo" className="co-upload__preview" /> : <div className="co-upload__placeholder"><ImagePlus /></div>}
                  <label className="co-upload__btn">
                    {uploading ? <Loader2 className="spinning" size={14} /> : 'Choose image'}
                    <input type="file" accept="image/*" hidden onChange={handleImageUpload} disabled={uploading} />
                  </label>
                </div>
              </label>
            </div>
          )}

          {step === 2 && (
            <div className="co-form">
              <p className="co-form__hint">How will this combo be put together?</p>
              <div className="co-pricing-choice">
                <button
                  type="button"
                  className={`co-pricing-opt ${form.comboStructure === 'BUNDLE' ? 'co-pricing-opt--active' : ''}`}
                  onClick={() => update('comboStructure', 'BUNDLE')}
                >
                  <Layers size={18} />
                  <span className="co-pricing-opt__title">Fixed bundle</span>
                  <span className="co-pricing-opt__sub">Same dishes every time, e.g. "Burger + Fries + Coke"</span>
                </button>
                <button
                  type="button"
                  className={`co-pricing-opt ${form.comboStructure === 'CHOICE' ? 'co-pricing-opt--active' : ''}`}
                  onClick={() => update('comboStructure', 'CHOICE')}
                >
                  <ListChecks size={18} />
                  <span className="co-pricing-opt__title">Let customer choose</span>
                  <span className="co-pricing-opt__sub">e.g. "Pick 1 starter + 1 main + 1 drink"</span>
                </button>
              </div>
            </div>
          )}

          {step === 3 && (
            <div className="co-form">
              <p className="co-form__hint">Choose how customers will be charged for this combo.</p>
              <div className="co-pricing-choice">
                <button type="button" className={`co-pricing-opt ${form.isFixedPrice ? 'co-pricing-opt--active' : ''}`} onClick={() => update('isFixedPrice', true)}>
                  <IndianRupee size={18} />
                  <span className="co-pricing-opt__title">Fixed price</span>
                  <span className="co-pricing-opt__sub">e.g. ₹99, ₹199 combos</span>
                </button>
                <button type="button" className={`co-pricing-opt ${!form.isFixedPrice ? 'co-pricing-opt--active' : ''}`} onClick={() => update('isFixedPrice', false)}>
                  <Percent size={18} />
                  <span className="co-pricing-opt__title">Discount on total</span>
                  <span className="co-pricing-opt__sub">e.g. 20% off combined price</span>
                </button>
              </div>

              {form.isFixedPrice ? (
                <label className="co-field">
                  <span>Fixed price (₹)</span>
                  <input type="number" min="1" value={form.fixedPrice} onChange={(e) => update('fixedPrice', e.target.value)} placeholder="199" />
                </label>
              ) : (
                <div className="co-discount-row">
                  <label className="co-field">
                    <span>Discount type</span>
                    <select value={form.discountType} onChange={(e) => update('discountType', e.target.value)}>
                      <option value="PERCENT">Percent (%)</option>
                      <option value="FLAT">Flat amount (₹)</option>
                    </select>
                  </label>
                  <label className="co-field">
                    <span>Discount value</span>
                    <input type="number" min="0" value={form.discount} onChange={(e) => update('discount', e.target.value)} placeholder={form.discountType === 'PERCENT' ? '20' : '50'} />
                  </label>
                </div>
              )}
              {form.comboStructure === 'CHOICE' && !form.isFixedPrice && (
                <p className="co-form__hint">For "let customer choose" combos, the discount applies to the lowest-priced combination — this is the price shown on the menu.</p>
              )}
            </div>
          )}

          {step === 4 && form.comboStructure === 'BUNDLE' && (
            <div className="co-form">
              <p className="co-form__hint">{selectedCount} dish{selectedCount !== 1 ? 'es' : ''} selected</p>
              <DishSearchList
                allDishes={allDishes}
                selectedMap={form.selectedDishes}
                onToggle={toggleDish}
                onQtyChange={changeQty}
                onSizeChange={changeSize}
              />
            </div>
          )}

          {step === 4 && form.comboStructure === 'CHOICE' && (
            <div className="co-form">
              <p className="co-form__hint">Build each group customers will pick from.</p>
              {form.choiceGroups.map((group, i) => (
                <ChoiceGroupCard
                  key={i}
                  group={group}
                  index={i}
                  allDishes={allDishes}
                  onUpdate={(g) => updateGroup(i, g)}
                  onRemove={() => removeGroup(i)}
                />
              ))}
              <button type="button" className="co-btn-secondary co-add-group" onClick={addGroup}>
                <Plus size={15} /> Add choice group
              </button>
            </div>
          )}

          {step === 5 && (
            <div className="co-form">
              <p className="co-form__hint">Optional — leave everything as default to keep this combo available all the time.</p>

              <div className="co-avail-dates">
                <label className="co-field">
                  <span>Valid from (optional)</span>
                  <input type="date" value={form.validFrom} onChange={(e) => update('validFrom', e.target.value)} />
                </label>
                <label className="co-field">
                  <span>Valid till (optional)</span>
                  <input type="date" value={form.validTill} onChange={(e) => update('validTill', e.target.value)} />
                </label>
              </div>

              <div className="co-field">
                <span>Available on</span>
                <div className="co-day-chips">
                  {DAYS.map((d) => (
                    <button
                      key={d.value}
                      type="button"
                      className={`co-day-chip ${form.availableDays.includes(d.value) ? 'co-day-chip--on' : ''}`}
                      onClick={() => toggleDay(d.value)}
                    >
                      {d.label}
                    </button>
                  ))}
                </div>
                <p className="co-form__hint" style={{ marginTop: 4 }}>
                  {form.availableDays.length === 0 ? 'No days selected means every day' : `Active on ${form.availableDays.length} day(s)`}
                </p>
              </div>

              <div className="co-field">
                <div className="co-allday-row">
                  <span>Time of day</span>
                  <label className="co-allday-toggle">
                    <input
                      type="checkbox"
                      checked={form.allDay}
                      onChange={(e) => update('allDay', e.target.checked)}
                    />
                    All day
                  </label>
                </div>
                {!form.allDay && (
                  <div className="co-time-row">
                    <input type="time" value={form.startTime} onChange={(e) => update('startTime', e.target.value)} />
                    <span>to</span>
                    <input type="time" value={form.endTime} onChange={(e) => update('endTime', e.target.value)} />
                  </div>
                )}
              </div>
            </div>
          )}

          {step === 6 && (
            <div className="co-form">
              <div className="co-review">
                <p className="co-review__name">{form.name || 'Untitled combo'}</p>
                {form.tag && <span className="co-card__tag co-review__tag">{form.tag}</span>}
                <p className="co-review__count">
                  {form.comboStructure === 'BUNDLE'
                    ? `${selectedCount} item${selectedCount !== 1 ? 's' : ''}`
                    : `${form.choiceGroups.length} choice group${form.choiceGroups.length !== 1 ? 's' : ''}`}
                </p>

                {form.comboStructure === 'BUNDLE' ? (
                  <ul className="co-review__list">
                    {Object.entries(form.selectedDishes).map(([dishId, sel]) => {
                      const dish = allDishes.find((d) => d._id === dishId);
                      return <li key={dishId}>{dish?.name || 'Dish'} {sel.size ? `(${sel.size})` : ''} × {sel.quantity}</li>;
                    })}
                  </ul>
                ) : (
                  <div className="co-review__groups">
                    {form.choiceGroups.map((g, i) => (
                      <div key={i} className="co-review__group">
                        <p className="co-review__group-name">{g.groupName || `Group ${i + 1}`} <span>(pick {g.minSelect}{g.maxSelect !== g.minSelect ? `–${g.maxSelect}` : ''})</span></p>
                        <ul className="co-review__list">
                          {Object.entries(g.options).map(([dishId, sel]) => {
                            const dish = allDishes.find((d) => d._id === dishId);
                            return <li key={dishId}>{dish?.name || 'Dish'} {sel.size ? `(${sel.size})` : ''}</li>;
                          })}
                        </ul>
                      </div>
                    ))}
                  </div>
                )}

                {(form.validFrom || form.validTill || form.availableDays.length > 0 || !form.allDay) && (
                  <div className="co-review__schedule">
                    {form.validFrom && <p>From {form.validFrom}</p>}
                    {form.validTill && <p>Till {form.validTill}</p>}
                    {form.availableDays.length > 0 && (
                      <p>On {form.availableDays.map((d) => DAYS.find((x) => x.value === d)?.label).join(', ')}</p>
                    )}
                    {!form.allDay && form.startTime && form.endTime && <p>{form.startTime} – {form.endTime}</p>}
                  </div>
                )}

                <div className="co-review__price">
                  <span>{form.comboStructure === 'CHOICE' ? 'Lowest possible total' : 'Combined menu price'}</span>
                  <strong>₹{livePricing.originalPrice}</strong>
                </div>
                <div className="co-review__price co-review__price--final">
                  <span>Customer pays{form.comboStructure === 'CHOICE' ? ' (from)' : ''}</span>
                  <strong>₹{livePricing.finalPrice}</strong>
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="co-modal__foot">
          {step > 1 ? <button className="co-btn-secondary" onClick={goBack}><ChevronLeft size={16} /> Back</button> : <span />}
          {step < 6 ? (
            <button className="co-btn-primary" onClick={goNext}>Next <ChevronRight size={16} /></button>
          ) : (
            <button className="co-btn-primary" onClick={handleSubmit} disabled={saving}>
              {saving ? <Loader2 className="spinning" size={16} /> : (isEdit ? 'Save changes' : 'Create combo')}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

/* ─── Delete confirm ─── */
const ConfirmDelete = ({ combo, onCancel, onConfirm, deleting }) => (
  <div className="co-modal-backdrop" onClick={onCancel}>
    <div className="co-confirm" onClick={(e) => e.stopPropagation()}>
      <p className="co-confirm__title">Delete "{combo.name}"?</p>
      <p className="co-confirm__sub">This combo will be removed from your menu. This can't be undone.</p>
      <div className="co-confirm__actions">
        <button className="co-btn-secondary" onClick={onCancel}>Cancel</button>
        <button className="co-btn-danger" onClick={onConfirm} disabled={deleting}>
          {deleting ? <Loader2 className="spinning" size={16} /> : 'Delete'}
        </button>
      </div>
    </div>
  </div>
);

/* ══════════════════════════════════════
   ROOT
══════════════════════════════════════ */
const CombosOffers = ({ cafeid }) => {
  const [combos, setCombos] = useState([]);
  const [allDishes, setAllDishes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [wizardOpen, setWizardOpen] = useState(false);
  const [editingCombo, setEditingCombo] = useState(null);
  const [deletingCombo, setDeletingCombo] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [busyId, setBusyId] = useState(null);

  const fetchCombos = useCallback(async () => {
    if (!cafeid) return;
    setLoading(true);
    setError('');
    try {
      const r = await fetch(`${host}/combo/list?cafeid=${cafeid}`, { headers: { Authorization: getToken() } });
      const d = await r.json();
      if (!r.ok || d.success === false) { setError(d.msg || 'Could not load combos'); return; }
      setCombos(d.data || []);
    } catch {
      setError('No connection. Please check your internet.');
    } finally {
      setLoading(false);
    }
  }, [cafeid]);

  const fetchDishes = useCallback(async () => {
    if (!cafeid) return;
    try {
      const r = await fetch(`${host}/masala/fetchAllDishes?cafeid=${cafeid}`, { headers: { Authorization: getToken() } });
      const d = await r.json();
      if (r.ok && d.success !== false) setAllDishes(d.data || []);
    } catch {
      // dish list only needed to open the wizard — the wizard shows an empty state if this fails
    }
  }, [cafeid]);

  useEffect(() => { fetchCombos(); fetchDishes(); }, [fetchCombos, fetchDishes]);

  const openCreate = () => { setEditingCombo(null); setWizardOpen(true); };
  const openEdit = (combo) => { setEditingCombo(combo); setWizardOpen(true); };
  const closeWizard = () => { setWizardOpen(false); setEditingCombo(null); setError(''); };
  const onSaved = () => { closeWizard(); fetchCombos(); };

  const handleToggle = async (combo) => {
    setBusyId(combo._id);
    setError('');
    try {
      const r = await fetch(`${host}/combo/toggle/${combo._id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', Authorization: getToken() },
        body: JSON.stringify({ isAvailable: !combo.isAvailable }),
      });
      const d = await r.json();
      if (!r.ok || d.success === false) { setError(d.msg || 'Could not update combo'); return; }
      await fetchCombos();
    } catch {
      setError('Network error — please try again');
    } finally {
      setBusyId(null);
    }
  };

  const confirmDelete = async () => {
    if (!deletingCombo) return;
    setDeleting(true);
    setError('');
    try {
      const r = await fetch(`${host}/combo/delete/${deletingCombo._id}`, { method: 'DELETE', headers: { Authorization: getToken() } });
      const d = await r.json();
      if (!r.ok || d.success === false) { setError(d.msg || 'Could not delete combo'); return; }
      setDeletingCombo(null);
      await fetchCombos();
    } catch {
      setError('Network error — please try again');
    } finally {
      setDeleting(false);
    }
  };

  const filteredCombos = combos.filter((c) => !search || c.name.toLowerCase().includes(search.toLowerCase()));
  const activeCount = combos.filter((c) => c.isAvailable).length;

  if (loading && combos.length === 0) {
    return (
      <div className="co">
        <div className="co-center">
          <Loader2 className="spinning" size={28} />
          <p className="co-muted">Loading your combos…</p>
        </div>
      </div>
    );
  }

  return (
    <div className="co">
      <header className="co-header">
        <div>
          <p className="co-header__title">Combos & Offers</p>
          <p className="co-header__sub">Bundle dishes together and offer them at a special price</p>
        </div>
        <button className="co-btn-primary" onClick={openCreate}><Plus size={16} /> Create combo</button>
      </header>

      <div className="co-stats">
        <div className="co-stat"><span className="co-stat__n">{combos.length}</span><span className="co-stat__l">Total combos</span></div>
        <div className="co-stat"><span className="co-stat__n co-stat__n--live">{activeCount}</span><span className="co-stat__l">Live now</span></div>
      </div>

      <div className="co-search-wrap">
        <Search size={16} />
        <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search combos…" />
      </div>

      {error && (
        <div className="co-toast">
          <AlertCircle size={16} />
          <span>{error}</span>
          <button className="co-toast__x" onClick={() => setError('')}>×</button>
        </div>
      )}

      {filteredCombos.length === 0 ? (
        <div className="co-empty">
          <div className="co-empty__icon"><Tag /></div>
          <p className="co-empty__title">{combos.length === 0 ? 'No combos yet' : 'No combos match your search'}</p>
          <p className="co-empty__sub">
            {combos.length === 0 ? 'Bundle a few dishes together and offer them at a special price to get started.' : 'Try a different search term.'}
          </p>
          {combos.length === 0 && <button className="co-btn-primary" onClick={openCreate}><Plus size={16} /> Create your first combo</button>}
        </div>
      ) : (
        <div className="co-grid">
          {filteredCombos.map((combo) => (
            <ComboCard key={combo._id} combo={combo} onEdit={openEdit} onDelete={setDeletingCombo} onToggle={handleToggle} busyId={busyId} />
          ))}
        </div>
      )}

      {wizardOpen && (
        <ComboWizard cafeid={cafeid} editingCombo={editingCombo} allDishes={allDishes} onClose={closeWizard} onSaved={onSaved} onError={setError} />
      )}

      {deletingCombo && (
        <ConfirmDelete combo={deletingCombo} onCancel={() => setDeletingCombo(null)} onConfirm={confirmDelete} deleting={deleting} />
      )}
    </div>
  );
};

export default CombosOffers;